// src/screens/Admin/navigation screens/support/TicketDetailDrawer.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import MyContext from "../../../MyContext";
import { StatusBadge, PriorityBadge } from "./TicketBadges";

// Lucide icons
import {
  Hash,
  MessageSquareText,
  Building2,
  Flag,
  User2,
  CalendarClock,
  Clock3,
  CheckCircle2,
} from "lucide-react";

function TicketDetailDrawer({ open, onClose, ticket }) {
  const { baseUrl, config } = useContext(MyContext);
  const [status, setStatus] = useState(ticket?.status || "O");
  const [saving, setSaving] = useState(false);

  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [newMsg, setNewMsg] = useState("");
  const listRef = useRef(null);

  const ticketId = ticket?.pk_ticket_id;

  useEffect(() => {
    setStatus(ticket?.status || "O");
  }, [ticket]);

  const loadConversation = async () => {
    if (!ticketId) return;
    setMsgLoading(true);
    try {
      const payload = { ticketid: String(ticketId) };
      const { data } = await axios.post(
        `${baseUrl}/api/Support/v1.0/getSupportTicketConversation`,
        payload,
        config
      );
      if (data.responseCode === "00") {
        setMessages(data.response || []);
      } else {
        toast.error(data.responseMessage || "Failed to load conversation");
        setMessages([]);
      }
    } catch (e) {
      toast.error("Error loading conversation");
      console.error(e);
      setMessages([]);
    } finally {
      setMsgLoading(false);
      setTimeout(() => {
        listRef.current?.scrollTo({ top: 999999, behavior: "smooth" });
      }, 50);
    }
  };

  useEffect(() => {
    if (open && ticketId) loadConversation();
  }, [open, ticketId]);

  const handleUpdateStatus = async () => {
    if (!ticketId) return;
    const confirm = await Swal.fire({
      title: "Update Status?",
      text: `Change status to ${status === "O" ? "Open" : status === "P" ? "In Progress" : "Closed"} for ticket #${ticket?.ticket_code}`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Update",
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    try {
      const payload = { userid: ticket.us_code, status, ticketid: String(ticketId) };
      const { data } = await axios.post(
        `${baseUrl}/api/Support/v1.0/updateSupportTicket`,
        payload,
        config
      );
      if (data.responseCode === "00") {
        toast.success("Status updated");
        onClose(true);
      } else {
        toast.error(data.responseMessage || "Failed to update status");
      }
    } catch (e) {
      toast.error("Error updating status");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const sendMessage = async () => {
    if (!ticketId) return;
    if (!newMsg.trim()) {
      return toast.info("Type a message first");
    }
    try {
      const payload = {
        ticketid: String(ticketId),
        userid: "3",
        message: newMsg.trim(),
         sender: "Admin"
      };
      const { data } = await axios.post(
        `${baseUrl}/api/Support/v1.0/insertSupportTicketConversation`,
        payload,
        config
      );
      if (data.responseCode === "00") {
        setNewMsg("");
        loadConversation();
      } else {
        toast.error(data.responseMessage || "Failed to send message");
      }
    } catch (e) {
      toast.error("Error sending message");
      console.error(e);
    }
  };

  const drawerClass = `offcanvas offcanvas-end ${open ? "show" : ""} admin-ticket-drawer`;
  const style = { visibility: open ? "visible" : "hidden" };

  // Equal-size, compact meta cards with icon + custom color tones
  const metaItems = useMemo(() => {
    if (!ticket) return [];
    return [
      {
        key: "Ticket Code",
        val: ticket.ticket_code || "-",
        icon: Hash,
        tone: "blue",
      },
      {
        key: "Subject",
        val: ticket.subject || "-",
        icon: MessageSquareText,
        tone: "violet",
      },
      {
        key: "Department",
        val: ticket.department || "-",
        icon: Building2,
        tone: "teal",
      },
      {
        key: "Priority",
        val: <PriorityBadge value={ticket.priority} />,
        icon: Flag,
        tone: "amber",
      },
      {
        key: "Customer Id",
        val: ticket.customer_id || "-",
        icon: User2,
        tone: "cyan",
      },
      {
        key: "Created",
        val: ticket.created_on || "-",
        icon: CalendarClock,
        tone: "rose",
      },
      {
        key: "Updated",
        val: ticket.updated_on || "-",
        icon: Clock3,
        tone: "indigo",
      },
      {
        key: "Closed On",
        val: ticket.closed_on || "-",
        icon: CheckCircle2,
        tone: "green",
      },
    ];
  }, [ticket]);
  // Helper: decide if a message is from Admin
// Helper: message is from Admin
const isAdminMsg = React.useCallback((m) => {
  if (m && typeof m.sender === "string") {
    return m.sender.toLowerCase() === "admin";
  }
  // fallback if 'sender' missing
  return m?.fk_user_id == null;
}, []);



  return (
    <div className={drawerClass} style={style} tabIndex="-1">
      <div className="offcanvas-header">
        <div>
          <h5 className="mb-1">
            Ticket #{ticket?.ticket_code}{" "}
            <StatusBadge code={status} className="ms-2" />
          </h5>
          <div className="small text-muted">{ticket?.subject}</div>
        </div>
        <button type="button" className="btn-close" onClick={() => onClose(false)} />
      </div>

      <div className="offcanvas-body d-flex flex-column">
        {/* META GRID */}
        <div className="admin-ticket-meta mb-3">
          <div className="admin-ticket-meta-grid">
            {metaItems.map((m) => {
              const Icon = m.icon;
              return (
                <div
                    key={m.key}
                    className={`admin-ticket-meta-item admin-ticket-tone-${m.tone}`}
                    title={m.key}
                >
                  <div className="admin-ticket-meta-icon">
                    <Icon size={18} />
                  </div>
                  <div className="admin-ticket-meta-content">
                    <div className="admin-ticket-meta-label">{m.key}</div>
                    <div className="admin-ticket-meta-value">{m.val}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* STATUS ACTION */}
        <div className="admin-ticket-status row g-2 align-items-end mb-3">
          <div className="col-7 col-md-4">
            <label className="form-label">Change Status</label>
            <select
              className="form-select"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="Open">Open</option>
              {/* <option value="P">In Progress</option> */}
              <option value="Close">Closed</option>
            </select>
          </div>
          <div className="col-5 col-md-3">
            <button
              className="btn btn-primary w-100"
              onClick={handleUpdateStatus}
              disabled={saving}
            >
              {saving ? "Saving..." : "Update"}
            </button>
          </div>
        </div>

        {/* CONVERSATION */}
        <div className="admin-ticket-chat card flex-grow-1 mb-3 w-100">
          <div className="card-header d-flex justify-content-between align-items-center ">
            <div className="fw-semibold">Conversation</div>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={loadConversation}
              disabled={msgLoading}
            >
              {msgLoading ? "Loading..." : "Refresh"}
            </button>
          </div>
        <div className="card-body admin-ticket-chat-body" ref={listRef}>
  {msgLoading ? (
    <div className="text-center py-3">
      <div className="spinner-border" role="status" />
    </div>
  ) : Array.isArray(messages) && messages.length > 0 ? (
    messages.map((m, idx) => {
      const adminSide = isAdminMsg(m);
      const label = adminSide ? "Admin" : "Client";
      return (
        <div
          key={m?.pk_conversation_id ?? idx}
          className={`admin-ticket-msg ${adminSide ? "is-admin" : "is-customer"}`}
        >
          <div className="admin-ticket-msg-bubble">
            {/* {m?.update_type ? (
              <div className="admin-ticket-msg-update chip">{m.update_type}</div>
            ) : null} */}
            <div className="admin-ticket-msg-text">{m?.message ?? ""}</div>
            <div className="admin-ticket-msg-meta">
              <span className="badge bg-light text-dark">{label}</span>
              <span className="text-muted small ms-2">{m?.created_on ?? ""}</span>
            </div>
          </div>
        </div>
      );
    })
  ) : (
    <div className="text-center text-muted">No messages yet.</div>
  )}
</div>

          <div className="card-footer">
            <div className="d-flex gap-2">
              <input
                className="form-control"
                placeholder="Type a message..."
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") sendMessage();
                }}
              />
              <button className="btn btn-success" onClick={sendMessage}>
                Send
              </button>
            </div>
          </div>
        </div>

        {/* CLOSE */}
        <div className="d-grid">
          <button
            className="btn btn-outline-secondary"
            onClick={() => onClose(false)}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default TicketDetailDrawer;
